import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../../../../common/Button";
import CustomInput from "../../../../common/CustomInput";
import {
  createAllergy,
  updateAllergy,
} from "../../../../../redux/modules/patient/actions";
import {isValidVigilanceSelection } from "../../../../../helpers/input-validations";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import CommonSlider from "../../../../common/CommonSlider";
import CommonSelect from "../../../clinical-services/services/select/common-creatable-select";
import {
  sourceOptions,
  severityOptions,
  categoryOptions,
  typeOptions,
  reactionOptions,
} from "../../../clinical-services/service-steps/clinical-assessment/data";
import AllergySelect from "../../../clinical-services/services/select/allergy-select";
import ReactionSelect from "../../../clinical-services/services/select/reaction-select";

export default function AllergyCreate({
  open,
  onClose,
  selectedAllergy: oldSelectAllergy,
  patientId,
  create = true,
  onSave = () => {},
}) {
  //State Variables
  const dispatch = useDispatch();

  const [selectedAllergy, setSelectedAllergy] = useState({
    id: create ? null : oldSelectAllergy.allergyGenCode,
    name: create ? null : oldSelectAllergy.allergyName,
  });
  const [type, setType] = useState(create ? null : oldSelectAllergy.type);
  const [category, setCategory] = useState(
    create ? null : oldSelectAllergy.category
  );
  const [reaction, setReaction] = useState(
    create ? null : {
      value: oldSelectAllergy.reaction,
      label: oldSelectAllergy.reaction,
    }
  );
  const [severity, setSeverity] = useState(
    create ? null : oldSelectAllergy.severity
  );
  const [source, setSource] = useState(
    create ? {label:'Patient', value:'patient'} : oldSelectAllergy.source
  );
  const [comments, setComments] = useState(
    create ? "" : oldSelectAllergy.comments
  );
  const [allergyNameInputMode, setAllergyNameInputMode] = useState(
    create ? false : oldSelectAllergy.allergyGenCode === null ? true : false
  );
  const [openUncodedAllergy, setOpenUncodedAllergy] = useState(false);
  const [addAnotherAllergy, setAddAnotherAllergy] = useState(false);

  useEffect(() => {
    setSource(create ?  {label:'Patient', value:'patient'}  : oldSelectAllergy.source);
  }, []);

  useEffect(() => {
    setSelectedAllergy({
      value: create
        ? null
        : oldSelectAllergy?.allergyGenCode === null
        ? oldSelectAllergy.allergyName
        : oldSelectAllergy.allergyGenCode,
      label: create ? null : oldSelectAllergy.allergyName,
    });

    setType(
      create
        ? null
        : {
            value: oldSelectAllergy.type,
            label: typeOptions.find(
              (item) => item.value === oldSelectAllergy.type
            )?.label,
          }
    );
    setCategory(
      create
        ? null
        : {
            value: oldSelectAllergy.category,
            label: categoryOptions.find(
              (item) => item.value === oldSelectAllergy.category
            ).label,
          }
    );
    setReaction(create ? null : {
      value: oldSelectAllergy.reaction,
      label: oldSelectAllergy.reaction,
    });
    setSeverity(
      create
        ? null
        : {
            value: oldSelectAllergy.severity,
            label: severityOptions.find(
              (item) => item.value === oldSelectAllergy.severity
            )?.label,
          }
    );
    setSource(
      create
        ?  {label:'Patient', value:'patient'} 
        : {
            value: oldSelectAllergy.source,
            label: sourceOptions.find(
              (item) => item.value === oldSelectAllergy.source
            ).label,
          }
    );
    setComments(create ? "" : oldSelectAllergy.comments);
    setAllergyNameInputMode(
      create ? false : oldSelectAllergy.allergyGenCode === null ? true : false
    );
  }, [oldSelectAllergy]);

  const [selectedAllergyError, setSelectedAllergyError] = useState(false);
  const [typeError, setTypeError] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [sourceError, setSourceError] = useState(false);
  const [severityError, setSeverityError] = useState(false);
  const [clicked, setClicked] = useState(false);

  const fieldsToCheck = [
    {
      field: selectedAllergy,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedAllergyError,
      alter: false,
    },
    {
      field: type,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setTypeError,
      alter: false,
    },
    {
      field: category,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setCategoryError,
      alter: false,
    },
    {
      field: source,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSourceError,
      alter: false,
    },
    {
      field: severity,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSeverityError,
      alter: false,
    },
  ];

  const resetForm = () => {
    setSelectedAllergy({ id: "", name: "" });
    setClicked(false);
    setCategoryError(false);
    setTypeError(false);
    setSourceError(false);
    setSeverityError(false);
    setSelectedAllergyError(false);
    setType(null);
    setCategory(null);
    setReaction("");
    setSeverity(null);
    setSource({label:'Patient', value:'patient'} );
    setComments("");
  };

  const manageStep = (addAnother) => {
    setClicked(true);
    let allFieldsValid = true;

    fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
      const hasError = !checkFn(field);
      setErrorFn(hasError);

      if (hasError) {
        allFieldsValid = false;
      }
    });
    if (allFieldsValid) {
      let currentAllergy = {
        allergyName: selectedAllergy.label,
        allergyGenCode: allergyNameInputMode ? null : selectedAllergy.value,
        type: type.value,
        category: category.value,
        carrierId: "carrierId",
        reaction: reaction?.label,
        severity: severity.value,
        source: source.value,
        status: "active",
        comments: comments,
        confirmedDate: null,
      };
      if (create) {
        let allergyObject = {
          noKnownAllergies: false,
          allergies: [currentAllergy],
        };
        dispatch(createAllergy(patientId, allergyObject));
        resetForm();
        if (!addAnother) {
          onClose();
        }
        // dispatch
      } else {
        dispatch(updateAllergy(oldSelectAllergy.id, currentAllergy));
        resetForm();
        onClose();
      }
      onSave();
      //dispatch(fetchAllergy(patientId));
    } else {
      setClicked(true);
    }
  };

  useEffect(() => {
    if (clicked) {
      fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
        const hasError = !checkFn(field);
        setErrorFn(hasError);
      });
    }
  }, [clicked, ...fieldsToCheck.map(({ field }) => field)]);

  return (
    <CommonSlider open={open} onClose={onClose}>
      <div className="flex flex-col flex-1 h-full overflow-y-auto ">
        <div className="relative flex-1">
          {/* Header */}
          <div className="px-8 py-6 bg-gray-20">
            <div className="flex items-start justify-between space-x-3">
              <div className="flex space-x-1">
                <h2 className="font-bold text-gray-900 text-display-xs">
                  {create ? "Add" : "Update"} Allergy
                </h2>
              </div>
              <div className="flex items-center h-7">
                <button
                  type="button"
                  className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                  onClick={() => onClose()}
                >
                  <span className="sr-only">Close panel</span>
                  <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
          <div className="px-8 py-6">
            <div className="grid grid-cols-12 gap-6">
              <CommonSelect
                onChange={(value) => setCategory(value)}
                isCreatable={true}
                options={categoryOptions}
                label="Category"
                isRequired={true}
                value={category}
                className="col-span-12"
                error={categoryError}
              />
              <AllergySelect
                onChange={(medicine) => setSelectedAllergy(medicine)}
                isRequired={true}
                onNewCreate={(value) => {
                  if (create) setAllergyNameInputMode(value);
                }}
                error={selectedAllergyError}
                value={selectedAllergy}
                placeholder="Search Allergy"
                disabled={!create}
                isClearable={create}
                showDropdownIndicator={create}
              />
              {allergyNameInputMode && (
                <p className="col-span-12 text-body-sm text-red-200 font-medium">
                  This is an UNCODED ALLERGY- No Clinical Check will be
                  performed.
                </p>
              )}
              <CommonSelect
                onChange={(value) => setType(value)}
                isCreatable={true}
                options={typeOptions}
                label="Type"
                isRequired={true}
                value={type}
                className="col-span-12"
                error={typeError}
              />
              <ReactionSelect 
                onChange={(value) => setReaction(value)}
                value={reaction}
                isRequired={true}
                defaultOptions={reactionOptions}
                label="Reaction"
                placeholder="Search Reaction Type"
                isCreatable={true}
              />
              <CommonSelect
                onChange={(value) => setSeverity(value)}
                isCreatable={true}
                options={severityOptions}
                label="Severity"
                isRequired={true}
                value={severity}
                className="col-span-12"
                error={severityError}
              />
              <CommonSelect
                onChange={(value) => {
                  setSource(value);
                }}
                isCreatable={true}
                options={sourceOptions}
                label="Source"
                isRequired={true}
                value={source}
                className="col-span-12"
                error={sourceError}
              />
              <CustomInput
                name="comments"
                className="col-span-12"
                label="Comments"
                placeholder="Comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                type="textarea"
                inputClassName="min-h-[48px] max-h-[72px] overflow-y-auto resize-none whitespace-pre-wrap break-words leading-6"
              />
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-center py-6">
          <div className="flex items-center justify-center space-x-5">
            <Button
              className="min-w-[200px]"
              text="Cancel"
              type="secondary"
              onClick={() => {
                resetForm();
                onClose();
              }}
            />
            {create && (
              <Button
                className="min-w-[200px]"
                text="Save & Add Another"
                type="primary"
                onClick={() => {
                  if (allergyNameInputMode) {
                    setAddAnotherAllergy(true);
                    setOpenUncodedAllergy(true);
                  } else {
                    manageStep(true);
                  }
                }}
              />
            )}
            <Button
              className="min-w-[200px]"
              text={create ? "Save" : "Update"}
              type="primary"
              onClick={() => {
                if (allergyNameInputMode) {
                  setOpenUncodedAllergy(true);
                } else {
                  manageStep();
                }
              }}
            />
          </div>
        </div>
        <ConfirmationModalNew
          open={openUncodedAllergy}
          title={"Uncoded Drug Alert"}
          type={"primary"}
          confirmText={"Continue"}
          cancelText="Cancel & Review"
          content={() => (
            <p>
              An uncoded ALLERGY has been added. Profile Analysis cannot be
              performed. A manual clinical check will be required.
            </p>
          )}
          onCross={() => {
            setAddAnotherAllergy(false);
            setOpenUncodedAllergy(false);
          }}
          handleClose={() => {
            setAddAnotherAllergy(false);
            setOpenUncodedAllergy(false);
          }}
          confirmAction={() => {
            if (addAnotherAllergy) {
              setAddAnotherAllergy(false);
              setAllergyNameInputMode(false);
              setOpenUncodedAllergy(false);
              manageStep(true);
            } else {
              setAllergyNameInputMode(false);
              setOpenUncodedAllergy(false);
              manageStep();
            }
          }}
        />
      </div>
    </CommonSlider>
  );
}
